// Clouseau — competitive-intelligence watcher.
//
// Pass 2 (real backend + core UX):
//   - reads competitors + signals from clouseau_competitor / clouseau_signal
//     (migration 105) via PostgREST anon (matches the rest of moreece);
//   - writes competitors via authorised-write (PIN-validated audit path);
//   - Add Competitor modal: real form, URL normalisation, palette cycle,
//     toast, optimistic prepend;
//   - Dossier modal: full structural layout (vitals strip, pulse list,
//     voices, hero SKU, story, journey arc, source chips). No animations
//     yet — Pass 3 lands the eye-tracking, spotlight cursor, radar, gauge
//     sweep, and journey path-draw.
//
// See moreece.com/handoff/CLOUSEAU-BRIEF.md (the why) and
// handoff/CLOUSEAU-CLAUDE-CODE-HANDOFF.md (the how).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { useToast } from '../lib/Toast';
import { humanizeError } from '../lib/humanizeError';
import './clouseau.css';

// Mercury palette tokens — single source of truth for this surface.
const TOKENS = {
  canvas:  '#F4EFE6',
  canvas2: '#EFEAE0',
  surface: '#FFFFFF',
  hairline: '#E8E6E0',
  ink:     '#0F1419',
  ink2:    '#3D4348',
  ink3:    '#8B9094',
  ink4:    '#C5C2BA',
  mint:    '#00A86B',
  warm:    '#B85C3C',
  lilac:   '#7C5CB8',
  blue:    '#4A5FBF',
};
const SANS  = '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif';
const SERIF = '"Tiempos", "Iowan Old Style", Georgia, "Times New Roman", serif';
const MONO  = 'ui-monospace, "SF Mono", Menlo, monospace';

const TYPE_META = {
  page:    { label: 'Page',    tone: 'mint' },
  site:    { label: 'Site',    tone: 'mint' },
  ad:      { label: 'Ad',      tone: 'warm' },
  price:   { label: 'Price',   tone: 'warm' },
  product: { label: 'Product', tone: 'lilac' },
  press:   { label: 'Press',   tone: 'blue' },
  email:   { label: 'Email',   tone: 'mint' },
};

// Mark palette cycle — a new competitor cycles through these so brand
// chips stay visually distinct without manual colour picking.
const MARK_PALETTES = [
  { bg: '#A6D5C2', fg: '#1F4A2C' },  // mint
  { bg: '#E8C8B8', fg: '#5C2C16' },  // copper
  { bg: '#D9C8E8', fg: '#3F2A66' },  // lilac
  { bg: '#C8D2EA', fg: '#1F2A66' },  // indigo
];

// ── Helpers ────────────────────────────────────────────────────────────
function fmtToday() {
  return new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
}
function fmtMonthYear(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
}
function relativeTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3600000);
  if (h < 1) return Math.max(1, Math.floor(ms / 60000)) + 'm';
  if (h < 24) return h + 'h';
  const d = Math.floor(h / 24);
  if (d < 14) return d + 'd';
  const w = Math.floor(d / 7);
  if (w < 8) return w + 'w';
  const mo = Math.floor(d / 30);
  if (mo < 24) return mo + 'mo';
  return Math.floor(d / 365) + 'y';
}
function stripStrong(s) { return String(s || '').replace(/<\/?strong>/g, ''); }
function hexToRgba(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
// Strip scheme + trailing slash. Per spec §4 "On submit".
function normaliseUrl(url) {
  return String(url || '').trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '');
}
function deriveMarkLetter(name) {
  const t = String(name || '').trim();
  const words = t.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return t.slice(0, 2).toUpperCase() || '?';
}
// §3.3 threat score: Σ(hot=35, watch=22, soft=10, dim=4) capped at 100.
function threatScore(signals) {
  const map = { hot: 35, watch: 22, soft: 10, dim: 4 };
  return Math.min(100, (signals || []).reduce((acc, s) => acc + (map[s.tone] || 0), 0));
}
function threatRead(score) {
  if (score >= 60) return { label: 'Elevated', color: TOKENS.warm };
  if (score >= 30) return { label: 'Watching', color: TOKENS.lilac };
  return { label: 'Quiet', color: TOKENS.mint };
}
// §7.2 headline rule.
function buildHeadline(competitors) {
  const all = competitors.flatMap(c => c.events);
  const movements = all.length;
  if (movements === 0) {
    return { lead: 'Quiet day.', tail: ` All ${competitors.length} watched brand${competitors.length === 1 ? '' : 's'} ${competitors.length === 1 ? 'is' : 'are'} still.` };
  }
  const active = competitors.filter(c => c.events.length > 0);
  const top = [...active].sort((a, b) => b.events.length - a.events.length)[0];
  const sample = top.events.slice(0, 2).map(e => stripStrong(e.line)).join(' and ').toLowerCase();
  const lead = `${movements} movement${movements === 1 ? '' : 's'}`;
  const tail = ` across ${active.length} competitor${active.length === 1 ? '' : 's'}. ${top.name} is the most active — ${sample}.`;
  return { lead, tail };
}

// ── Topbar ─────────────────────────────────────────────────────────────
// Eyes are HTML div circles so the pupils can be transformed individually
// via the document pointermove handler. CSS provides the blink keyframe.
function EyesGlyph() {
  return (
    <span className="cl-eyes" aria-hidden="true">
      <span className="cl-eye"><span className="cl-pupil" /></span>
      <span className="cl-eye"><span className="cl-pupil" /></span>
    </span>
  );
}
function Topbar({ onAddClick }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '24px 36px 16px',
      borderBottom: `1px solid ${TOKENS.hairline}`,
      background: `linear-gradient(180deg, #FAF8F2 0%, ${TOKENS.canvas} 100%)`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <EyesGlyph />
        <span style={{
          fontFamily: SANS, fontSize: 19, fontWeight: 500,
          color: TOKENS.ink, letterSpacing: '-0.01em',
        }}>Clouseau</span>
      </div>
      <button
        type="button"
        onClick={onAddClick}
        style={{
          padding: '8px 16px', borderRadius: 999,
          border: 'none', cursor: 'pointer',
          background: TOKENS.mint, color: '#FFFFFF',
          fontFamily: SANS, fontSize: 13, fontWeight: 500,
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}
      >
        <span style={{ fontSize: 14, lineHeight: 1 }}>+</span> Add competitor
      </button>
    </div>
  );
}

function TodaySummary({ competitors }) {
  const date = useMemo(fmtToday, []);
  const { lead, tail } = useMemo(() => buildHeadline(competitors), [competitors]);
  return (
    <div style={{ padding: '20px 36px 24px' }}>
      <div style={{
        fontFamily: MONO, fontSize: 10, letterSpacing: '0.12em',
        textTransform: 'uppercase', color: TOKENS.ink3,
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: TOKENS.mint }} />
        Today, {date}
      </div>
      <h1 style={{
        margin: '8px 0 0',
        fontFamily: SANS, fontSize: 28, fontWeight: 500,
        letterSpacing: '-0.02em', color: TOKENS.ink, lineHeight: 1.25,
      }}>
        <em style={{ fontStyle: 'normal', fontWeight: 600, color: TOKENS.mint }}>{lead}</em>{tail}
      </h1>
    </div>
  );
}

// ── Card ───────────────────────────────────────────────────────────────
function CompetitorCard({ competitor, onClick }) {
  const urgent = competitor.events.find(e => e.urgent);
  const updates = competitor.events.length;
  const isQueued = competitor.queued && updates === 0;
  return (
    <li
      className="cl-card"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      style={{
        listStyle: 'none',
        background: TOKENS.surface, border: `1px solid ${TOKENS.hairline}`,
        borderRadius: 14,
        boxShadow: '0 1px 2px rgba(15,20,25,0.04)',
        cursor: 'pointer', overflow: 'hidden',
        transition: 'box-shadow 160ms, border-color 160ms, transform 160ms',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 6px 22px rgba(15,20,25,0.07)';
        e.currentTarget.style.borderColor = 'rgba(0,168,107,0.28)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,20,25,0.04)';
        e.currentTarget.style.borderColor = TOKENS.hairline;
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={{
        padding: '18px 20px 14px', borderBottom: `1px solid ${TOKENS.hairline}`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 9,
          background: competitor.mark.bg, color: competitor.mark.fg,
          border: `1px solid ${TOKENS.hairline}`,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: SANS, fontSize: 13, fontWeight: 600, flexShrink: 0,
        }}>{competitor.mark.letter}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: SANS, fontSize: 15.5, fontWeight: 500, color: TOKENS.ink }}>{competitor.name}</div>
          <div style={{
            fontFamily: MONO, fontSize: 10.5, color: TOKENS.ink3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{competitor.url}</div>
        </div>
        {updates > 0 ? (
          <span style={{
            padding: '4px 10px', borderRadius: 999,
            background: 'rgba(0,168,107,0.08)', color: TOKENS.mint,
            fontFamily: SANS, fontSize: 11, fontWeight: 500,
            display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
          }}>
            <span className="cl-active-dot" />
            {updates} update{updates === 1 ? '' : 's'}
          </span>
        ) : (
          <span style={{
            padding: '4px 10px', borderRadius: 999,
            background: TOKENS.canvas2, color: TOKENS.ink3,
            fontFamily: SANS, fontSize: 11, fontWeight: 500, flexShrink: 0,
          }}>{isQueued ? 'Sweep queued' : 'Quiet today'}</span>
        )}
      </div>

      {urgent && <UrgentBanner event={urgent} />}

      {competitor.events.length === 0 ? (
        <div style={{
          padding: '22px 20px', display: 'flex', alignItems: 'center', gap: 10,
          fontFamily: SANS, fontSize: 13, color: TOKENS.ink3,
        }}>
          <span style={{
            width: 22, height: 22, borderRadius: '50%',
            border: `1px solid ${TOKENS.hairline}`,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: TOKENS.ink4, fontFamily: MONO, fontSize: 11,
          }}>·</span>
          {isQueued
            ? 'First sweep queued — anything notable will land here.'
            : `Nothing to report.${competitor.lastActivityRel ? ` Last activity ${competitor.lastActivityRel} ago.` : ''}`}
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {competitor.events.map((ev, i) => (
            <FeedRow key={ev.id || i} event={ev} isLast={i === competitor.events.length - 1} />
          ))}
        </ul>
      )}
    </li>
  );
}
function UrgentBanner({ event }) {
  const cat = ({ page: 'Website', site: 'Website', price: 'Pricing', product: 'Product', ad: 'Ads', press: 'Press', email: 'Email' })[event.type] || 'Update';
  return (
    <div style={{
      padding: '12px 18px 13px',
      borderLeft: `3px solid ${TOKENS.warm}`,
      background: 'linear-gradient(90deg, rgba(184,92,60,0.10), rgba(184,92,60,0.02))',
      borderBottom: `1px solid ${TOKENS.hairline}`,
    }}>
      <div style={{
        fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.1em',
        textTransform: 'uppercase', color: TOKENS.warm,
        display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 6,
      }}>
        <span className="cl-urgent-dot" />
        URGENT · {cat} change · {event.time}
      </div>
      <div
        style={{ fontFamily: SANS, fontSize: 14, color: TOKENS.ink, fontWeight: 500, lineHeight: 1.4 }}
        dangerouslySetInnerHTML={{ __html: event.line }}
      />
      {event.urgentNote && (
        <div style={{
          marginTop: 4, fontFamily: SANS, fontSize: 12.5, color: TOKENS.ink2,
          fontStyle: 'italic', lineHeight: 1.45,
        }}>{event.urgentNote}</div>
      )}
    </div>
  );
}
function FeedRow({ event, isLast }) {
  const meta = TYPE_META[event.type] || { label: event.type, tone: 'blue' };
  const toneColor = TOKENS[meta.tone] || TOKENS.blue;
  return (
    <li style={{
      padding: '14px 20px', display: 'flex', gap: 12,
      borderBottom: isLast ? 'none' : `1px solid ${TOKENS.hairline}`,
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
        background: hexToRgba(toneColor, 0.10), color: toneColor,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: MONO, fontSize: 11, fontWeight: 600,
      }}>{meta.label[0]}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          fontFamily: MONO, fontSize: 10, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: toneColor, marginRight: 8,
        }}>{meta.label}</span>
        <span
          style={{ fontFamily: SANS, fontSize: 13, color: TOKENS.ink, lineHeight: 1.45 }}
          dangerouslySetInnerHTML={{ __html: event.line }}
        />
        {event.detail && (
          <div style={{ marginTop: 4, fontFamily: SANS, fontSize: 12, color: TOKENS.ink2, lineHeight: 1.5 }}>
            {event.detail}
          </div>
        )}
        {event.source && (
          <div style={{
            marginTop: 4, fontFamily: MONO, fontSize: 10, letterSpacing: '0.06em',
            color: TOKENS.ink3, textTransform: 'uppercase',
          }}>{event.source} <span aria-hidden style={{ marginLeft: 2 }}>↗</span></div>
        )}
      </div>
      <div style={{
        flexShrink: 0, fontFamily: MONO, fontSize: 10, letterSpacing: '0.06em',
        textTransform: 'uppercase', color: TOKENS.ink3,
      }}>{event.time}</div>
    </li>
  );
}

// ── Modal shell ────────────────────────────────────────────────────────
function ModalShell({ onClose, children, width = 720 }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);
  return createPortal(
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
      background: 'rgba(15,20,25,0.42)',
      backdropFilter: 'blur(8px) saturate(120%)',
      WebkitBackdropFilter: 'blur(8px) saturate(120%)',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: `min(${width}px, calc(100vw - 48px))`,
        maxHeight: 'calc(100vh - 80px)',
        background: TOKENS.surface, borderRadius: 16,
        boxShadow: '0 30px 80px rgba(15,20,25,0.18)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>{children}</div>
    </div>,
    document.body,
  );
}

// ── Add Competitor modal — real ───────────────────────────────────────
function AddCompetitorModal({ session, onClose, onAdded, takenCount }) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [runFirst, setRunFirst] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const toast = useToast();

  const canSubmit = name.trim() && url.trim() && !submitting;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setErr('');
    try {
      const cleanUrl = normaliseUrl(url);
      if (!cleanUrl) { setErr('Website looks empty after cleanup.'); setSubmitting(false); return; }
      const palette = MARK_PALETTES[(takenCount || 0) % MARK_PALETTES.length];
      const payload = {
        name: name.trim(),
        url: cleanUrl,
        mark_letter: deriveMarkLetter(name),
        mark_bg: palette.bg,
        mark_fg: palette.fg,
        sources: [],
        created_by: session?.code || null,
      };
      const inserted = await authWrite(session, 'clouseau_competitor', 'insert', payload);
      const row = Array.isArray(inserted) ? inserted[0] : inserted;
      onAdded?.(row, runFirst);
      toast.success(`Now watching ${row.name}.`);
      onClose?.();
    } catch (e) {
      const { title, body } = humanizeError(e, 'Could not add');
      setErr(`${title}: ${body}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell onClose={onClose} width={520}>
      <div style={{ padding: '24px 28px', borderBottom: `1px solid ${TOKENS.hairline}` }}>
        <div style={{
          fontFamily: MONO, fontSize: 10, letterSpacing: '0.12em',
          textTransform: 'uppercase', color: TOKENS.mint,
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: TOKENS.mint }} />
          New brand to watch
        </div>
        <div style={{ fontFamily: SANS, fontSize: 22, fontWeight: 500, color: TOKENS.ink, marginTop: 6 }}>
          Add competitor
        </div>
        <div style={{ fontFamily: SANS, fontSize: 13, color: TOKENS.ink2, marginTop: 6, lineHeight: 1.5 }}>
          Clouseau will start watching their site, ads, press, and team. Anything notable lands here.
        </div>
      </div>

      <div style={{ padding: '22px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field label="Brand name" value={name} onChange={setName} placeholder="e.g. Liquid Death" autoFocus />
        <Field label="Website" value={url} onChange={setUrl} placeholder="liquiddeath.com" onEnter={submit} />

        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '12px 14px', borderRadius: 10,
          background: TOKENS.canvas, border: `1px solid ${TOKENS.hairline}`,
          fontFamily: SANS, fontSize: 12.5, color: TOKENS.ink2, cursor: 'pointer',
          lineHeight: 1.5,
        }}>
          <input
            type="checkbox" checked={runFirst} onChange={e => setRunFirst(e.target.checked)}
            style={{ marginTop: 2 }}
          />
          Run a first sweep now and surface any movement from the last 7 days.
        </label>

        {err && (
          <div style={{
            padding: '10px 12px', borderRadius: 8,
            background: 'rgba(184,92,60,0.10)', border: `1px solid ${hexToRgba(TOKENS.warm, 0.3)}`,
            color: TOKENS.warm, fontFamily: SANS, fontSize: 12,
          }}>{err}</div>
        )}
      </div>

      <div style={{
        padding: '16px 28px', borderTop: `1px solid ${TOKENS.hairline}`,
        display: 'flex', gap: 10, justifyContent: 'flex-end',
      }}>
        <button type="button" onClick={onClose} style={ghostBtn}>Cancel</button>
        <button type="button" onClick={submit} disabled={!canSubmit} style={{
          ...primaryBtn, opacity: canSubmit ? 1 : 0.5,
          cursor: canSubmit ? 'pointer' : 'not-allowed',
        }}>{submitting ? 'Watching…' : 'Start watching'}</button>
      </div>
    </ModalShell>
  );
}
function Field({ label, value, onChange, placeholder, autoFocus, onEnter }) {
  return (
    <div>
      <div style={{
        fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.12em',
        textTransform: 'uppercase', color: TOKENS.ink3, marginBottom: 6,
      }}>{label}</div>
      <input
        autoFocus={autoFocus}
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && onEnter) { e.preventDefault(); onEnter(); } }}
        placeholder={placeholder}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '10px 12px', borderRadius: 10,
          border: `1px solid ${TOKENS.hairline}`, background: TOKENS.canvas,
          fontFamily: SANS, fontSize: 13, color: TOKENS.ink, outline: 'none',
        }}
      />
    </div>
  );
}
const primaryBtn = {
  padding: '8px 16px', borderRadius: 999,
  border: 'none', background: TOKENS.mint, color: '#FFFFFF',
  fontFamily: SANS, fontSize: 13, fontWeight: 500,
};
const ghostBtn = {
  padding: '8px 16px', borderRadius: 999,
  border: `1px solid ${TOKENS.hairline}`, background: TOKENS.surface, color: TOKENS.ink2,
  fontFamily: SANS, fontSize: 13, fontWeight: 500, cursor: 'pointer',
};

// ── Dossier modal — real ──────────────────────────────────────────────
function Dossier({ competitorRow, onClose }) {
  const [signals, setSignals] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const since = new Date(); since.setDate(since.getDate() - 90);
      const { data } = await aws.from('clouseau_signal')
        .select('*').eq('competitor_id', competitorRow.id)
        .gte('detected_at', since.toISOString())
        .order('detected_at', { ascending: false }).limit(200);
      if (!cancelled) setSignals(data || []);
    })();
    return () => { cancelled = true; };
  }, [competitorRow.id]);

  const c = competitorRow;
  const story = Array.isArray(c.story) ? c.story : [];
  const voices = Array.isArray(c.voices) ? c.voices : [];
  const timeline = Array.isArray(c.timeline) ? c.timeline : [];
  const sources = Array.isArray(c.sources) ? c.sources : [];
  const hero = c.hero || null;

  const score = signals ? threatScore(signals) : 0;
  const read = threatRead(score);
  const yearsWatched = Math.max(0, Math.floor((Date.now() - new Date(c.watched_since).getTime()) / (365 * 86400000)));
  const lastSig = signals && signals[0] ? signals[0] : null;

  return (
    <ModalShell onClose={onClose} width={1080}>
      {/* Head */}
      <div style={{ padding: '24px 28px', borderBottom: `1px solid ${TOKENS.hairline}`, display: 'flex', gap: 16 }}>
        <div style={{
          width: 56, height: 56, borderRadius: 14,
          background: c.mark_bg, color: c.mark_fg,
          border: `1px solid ${TOKENS.hairline}`,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: SANS, fontSize: 18, fontWeight: 600, flexShrink: 0,
        }}>{c.mark_letter}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: MONO, fontSize: 10, letterSpacing: '0.12em',
            textTransform: 'uppercase', color: TOKENS.ink3,
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: TOKENS.mint }} />
            Brand dossier · watched since {fmtMonthYear(c.watched_since)}
          </div>
          <div style={{ fontFamily: SANS, fontSize: 24, fontWeight: 500, color: TOKENS.ink, marginTop: 6 }}>{c.name}</div>
          <div style={{ fontFamily: MONO, fontSize: 11, color: TOKENS.ink3, marginTop: 4 }}>
            {c.url}{c.tagline ? ` · ${c.tagline}` : ''}
          </div>
        </div>
        <Radar />
        <button type="button" onClick={onClose} aria-label="Close" style={{
          width: 30, height: 30, borderRadius: '50%',
          border: `1px solid ${TOKENS.hairline}`, background: TOKENS.surface,
          color: TOKENS.ink3, cursor: 'pointer', flexShrink: 0,
        }}>×</button>
      </div>

      {/* Vitals strip */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1,
        background: TOKENS.hairline,
        borderBottom: `1px solid ${TOKENS.hairline}`,
      }}>
        <Vital label="Watched" value={`${yearsWatched} yr${yearsWatched === 1 ? '' : 's'}`} foot={`since ${fmtMonthYear(c.watched_since)}`} />
        <Vital label="Signals · 90d" value={signals ? String(signals.length) : '…'} foot="last 90 days" />
        <Vital label="Last move" value={lastSig ? relativeTime(lastSig.detected_at) : '—'} foot={lastSig ? lastSig.type : 'no recent activity'} />
        <Vital
          label="Threat read"
          customValue={<ThreatGauge score={score} color={read.color} label={read.label} />}
          foot={`score ${score}/100`}
        />
        <Vital label="First seen" value={c.first_seen ? c.first_seen.split(',')[0] || c.first_seen : '—'} foot={c.hq || '—'} />
      </div>

      {/* Body — scrolls */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div className="cl-dossier-body" style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 28, padding: '24px 28px' }}>
          {/* Left col */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <Section label="Pulse · 90d">
              {signals === null ? (
                <Empty>Loading…</Empty>
              ) : signals.length === 0 ? (
                <Empty>No signals yet — sweep workers haven't surfaced anything for this brand.</Empty>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {signals.map(s => <PulseRow key={s.id} signal={s} />)}
                </ul>
              )}
            </Section>

            <Section label="Founder & exec voices">
              {voices.length === 0 ? (
                <Empty>No voices captured yet.</Empty>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {voices.map((v, i) => <VoiceBubble key={i} voice={v} />)}
                </div>
              )}
            </Section>
          </div>

          {/* Right col */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <Section label="Hero SKU · price trajectory">
              {hero ? <HeroCard hero={hero} /> : <Empty>No hero SKU configured yet.</Empty>}
            </Section>

            <Section label="In a sentence">
              {story.length === 0 ? (
                <Empty>No editorial summary yet.</Empty>
              ) : (
                <>
                  <p style={{
                    margin: 0, fontFamily: SERIF, fontSize: 17, color: TOKENS.ink,
                    lineHeight: 1.55, textWrap: 'pretty',
                  }}>{story[0]}</p>
                  <div className="cl-dossier-facts" style={{
                    marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 1, background: TOKENS.hairline, borderRadius: 10, overflow: 'hidden',
                    border: `1px solid ${TOKENS.hairline}`,
                  }}>
                    <FactCell label="HQ" value={c.hq || '—'} />
                    <FactCell label="Stage" value={c.stage || '—'} />
                    <FactCell label="Position" value={c.tagline || '—'} />
                  </div>
                  {story.length > 1 && (
                    <details style={{ marginTop: 14 }}>
                      <summary style={{
                        fontFamily: MONO, fontSize: 10, letterSpacing: '0.12em',
                        textTransform: 'uppercase', color: TOKENS.ink3, cursor: 'pointer',
                      }}>+ More context</summary>
                      <div style={{
                        marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10,
                        fontFamily: SANS, fontSize: 13, color: TOKENS.ink2, lineHeight: 1.6,
                      }}>
                        {story.slice(1).map((p, i) => <p key={i} style={{ margin: 0 }}>{p}</p>)}
                      </div>
                    </details>
                  )}
                </>
              )}
            </Section>
          </div>
        </div>

        {/* Journey arc */}
        <Section label="Journey" style={{ padding: '24px 28px', borderTop: `1px solid ${TOKENS.hairline}` }}>
          {timeline.length === 0 ? (
            <Empty>No timeline entries yet.</Empty>
          ) : (
            <JourneyArc points={timeline} />
          )}
        </Section>

        {/* Footer */}
        <div style={{
          padding: '16px 28px', borderTop: `1px solid ${TOKENS.hairline}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
        }}>
          <span style={{
            fontFamily: MONO, fontSize: 10, letterSpacing: '0.12em',
            textTransform: 'uppercase', color: TOKENS.ink3,
          }}>First seen · {c.first_seen || fmtMonthYear(c.watched_since)}</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {sources.map((s, i) => (
              <span key={i} style={{
                padding: '4px 10px', borderRadius: 999,
                background: TOKENS.canvas, border: `1px solid ${TOKENS.hairline}`,
                fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: TOKENS.ink3,
              }}>{s}</span>
            ))}
            {sources.length === 0 && (
              <span style={{ fontFamily: MONO, fontSize: 10, color: TOKENS.ink4 }}>no sources yet</span>
            )}
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

function Radar() {
  // Three concentric rings + a conic-gradient sweep + a translating blip
  // (CSS-driven, see clouseau.css). Hides on widths <= 880px per spec.
  return (
    <div className="cl-radar" aria-hidden="true">
      <span className="cl-radar-ring r1" />
      <span className="cl-radar-ring r2" />
      <span className="cl-radar-ring r3" />
      <span className="cl-radar-sweep" />
      <span className="cl-radar-blip" />
    </div>
  );
}

// Semicircular SVG gauge keyed off the threat score (§3.3). Path length
// is 78.5; CSS animates stroke-dasharray from `0 78.5` to the score's
// fraction over 1100ms.
function ThreatGauge({ score, color, label }) {
  const pct = Math.min(100, Math.max(0, score));
  const fill = (pct / 100) * 78.5;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <svg className="cl-gauge" width="60" height="36" viewBox="0 0 60 36" aria-hidden="true">
        <path className="cl-gauge-bg" d="M5,30 A25,25 0 0 1 55,30" />
        <path
          className="cl-gauge-fg"
          d="M5,30 A25,25 0 0 1 55,30"
          style={{ stroke: color, '--gauge-fill': fill }}
        />
      </svg>
      <span style={{
        fontFamily: SANS, fontSize: 22, fontWeight: 500,
        color, lineHeight: 1, letterSpacing: '-0.01em',
      }}>{label}</span>
    </div>
  );
}

function Vital({ label, value, foot, valueColor, customValue }) {
  return (
    <div style={{ background: TOKENS.surface, padding: '14px 16px' }}>
      <div style={{
        fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.12em',
        textTransform: 'uppercase', color: TOKENS.ink3,
      }}>{label}</div>
      {customValue ? (
        <div style={{ marginTop: 4 }}>{customValue}</div>
      ) : (
        <div style={{
          fontFamily: SANS, fontSize: 28, fontWeight: 500,
          color: valueColor || TOKENS.ink, marginTop: 4, lineHeight: 1,
          letterSpacing: '-0.01em',
        }}>{value}</div>
      )}
      {foot && (
        <div style={{ fontFamily: SANS, fontSize: 11.5, color: TOKENS.ink3, marginTop: 6 }}>{foot}</div>
      )}
    </div>
  );
}
function Section({ label, style, children }) {
  return (
    <section style={style}>
      <div style={{
        fontFamily: MONO, fontSize: 10, letterSpacing: '0.12em',
        textTransform: 'uppercase', color: TOKENS.ink3, marginBottom: 12,
      }}>{label}</div>
      {children}
    </section>
  );
}
function Empty({ children }) {
  return (
    <div style={{
      padding: '14px 16px', borderRadius: 10,
      background: TOKENS.canvas, border: `1px dashed ${TOKENS.hairline}`,
      fontFamily: SANS, fontSize: 12.5, color: TOKENS.ink3, lineHeight: 1.5,
    }}>{children}</div>
  );
}
function FactCell({ label, value }) {
  return (
    <div style={{ background: TOKENS.surface, padding: '10px 12px' }}>
      <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: TOKENS.ink3 }}>{label}</div>
      <div style={{ fontFamily: SANS, fontSize: 12.5, color: TOKENS.ink, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function PulseRow({ signal }) {
  const tone = signal.tone || 'dim';
  const toneColor = ({ hot: TOKENS.warm, watch: TOKENS.lilac, soft: TOKENS.mint, dim: TOKENS.ink3 })[tone];
  return (
    <li style={{
      position: 'relative', paddingLeft: 14,
      borderLeft: `3px solid ${toneColor}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{ fontFamily: SANS, fontSize: 13.5, color: TOKENS.ink, lineHeight: 1.5 }}
            dangerouslySetInnerHTML={{ __html: signal.line }}
          />
          {signal.detail && (
            <div style={{ marginTop: 4, fontFamily: SANS, fontSize: 12, color: TOKENS.ink2, lineHeight: 1.5 }}>
              {signal.detail}
            </div>
          )}
          {signal.source_label && (
            <div style={{
              marginTop: 4, fontFamily: MONO, fontSize: 10, letterSpacing: '0.06em',
              color: TOKENS.ink3, textTransform: 'uppercase',
            }}>{signal.source_label}</div>
          )}
        </div>
        <span style={{
          flexShrink: 0, fontFamily: MONO, fontSize: 10, letterSpacing: '0.06em',
          textTransform: 'uppercase', color: TOKENS.ink3,
        }}>{relativeTime(signal.detected_at)}</span>
      </div>
    </li>
  );
}

function VoiceBubble({ voice }) {
  const initials = String(voice.who || '?').split(/\s+/).map(w => w[0] || '').slice(0, 2).join('').toUpperCase();
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <div style={{
        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
        background: hexToRgba(TOKENS.lilac, 0.12), color: TOKENS.lilac,
        border: `1px solid ${hexToRgba(TOKENS.lilac, 0.25)}`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: SANS, fontSize: 12, fontWeight: 600,
      }}>{initials}</div>
      <div style={{
        flex: 1, position: 'relative',
        padding: '12px 14px',
        borderRadius: 12,
        background: TOKENS.canvas,
        borderLeft: `2px solid ${TOKENS.lilac}`,
        border: `1px solid ${TOKENS.hairline}`,
      }}>
        <div style={{ fontFamily: SERIF, fontSize: 14, color: TOKENS.ink, lineHeight: 1.55 }}>
          “{voice.quote}”
        </div>
        <div style={{
          marginTop: 6, fontFamily: MONO, fontSize: 10, letterSpacing: '0.06em',
          textTransform: 'uppercase', color: TOKENS.ink3,
        }}>{voice.who}{voice.source ? ` · ${voice.source}` : ''}</div>
      </div>
    </div>
  );
}

function HeroCard({ hero }) {
  const points = Array.isArray(hero.points) ? hero.points : [];
  const delta = hero.delta_pct;
  const deltaUp = typeof delta === 'number' && delta > 0;
  const W = 320, H = 72;
  const path = useMemo(() => {
    if (points.length < 2) return '';
    const xs = points.map((_, i) => (i / (points.length - 1)) * (W - 8) + 4);
    const ys = (() => {
      const vals = points.map(p => p.value || 0);
      const min = Math.min(...vals), max = Math.max(...vals);
      const range = max - min || 1;
      return vals.map(v => H - 6 - ((v - min) / range) * (H - 12));
    })();
    return xs.map((x, i) => (i === 0 ? `M${x},${ys[i]}` : `L${x},${ys[i]}`)).join(' ');
  }, [points]);
  const lastX = points.length ? ((points.length - 1) / Math.max(points.length - 1, 1)) * (W - 8) + 4 : 0;
  const lastY = (() => {
    if (points.length < 1) return 0;
    const vals = points.map(p => p.value || 0);
    const min = Math.min(...vals), max = Math.max(...vals);
    const range = max - min || 1;
    return H - 6 - ((vals[vals.length - 1] - min) / range) * (H - 12);
  })();

  return (
    <div style={{
      padding: '14px 16px', border: `1px solid ${TOKENS.hairline}`, borderRadius: 12,
      background: TOKENS.surface,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ fontFamily: SANS, fontSize: 14, color: TOKENS.ink, fontWeight: 500 }}>{hero.name || 'Hero SKU'}</div>
        {typeof delta === 'number' && (
          <div style={{
            fontFamily: MONO, fontSize: 11.5, fontWeight: 600,
            color: deltaUp ? TOKENS.warm : TOKENS.mint,
          }}>{deltaUp ? '+' : ''}{delta.toFixed(1)}%</div>
        )}
      </div>
      {points.length >= 2 ? (
        <svg width={W} height={H} style={{ display: 'block', marginTop: 10, width: '100%', maxWidth: W }} viewBox={`0 0 ${W} ${H}`}>
          <path d={path} fill="none" stroke={deltaUp ? TOKENS.warm : TOKENS.mint} strokeWidth="2" />
          <circle cx={lastX} cy={lastY} r="4" fill={deltaUp ? TOKENS.warm : TOKENS.mint} />
        </svg>
      ) : (
        <Empty>Not enough price points yet for a sparkline.</Empty>
      )}
      <div style={{
        marginTop: 8, display: 'flex', justifyContent: 'space-between',
        fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.06em',
        textTransform: 'uppercase', color: TOKENS.ink3,
      }}>
        <span>{points[0]?.label || ''}</span>
        <span>{hero.current_label || (points[points.length - 1] && `${points[points.length - 1].value} ${hero.unit || ''}`)}</span>
        <span>{points[points.length - 1]?.label || ''}</span>
      </div>
    </div>
  );
}

function JourneyArc({ points }) {
  const W = 720, H = 170;
  const N = points.length;
  if (N === 0) return null;
  const xs = points.map((_, i) => 40 + (i / Math.max(N - 1, 1)) * (W - 80));
  const ys = points.map((_, i) => H / 2 + Math.sin(i * Math.PI / Math.max(N - 1, 1)) * 28 - 14);
  const toneColor = (t) => ({ soft: TOKENS.mint, warn: TOKENS.warm, blue: TOKENS.blue, watch: TOKENS.lilac, dim: TOKENS.ink3 })[t] || TOKENS.ink3;
  // Build a quadratic-curve path through the points (simple chained smoothing).
  let d = '';
  for (let i = 0; i < N; i++) {
    if (i === 0) d += `M${xs[i]},${ys[i]}`;
    else {
      const cx = (xs[i - 1] + xs[i]) / 2;
      d += ` Q${cx},${ys[i - 1]} ${xs[i]},${ys[i]}`;
    }
  }
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      <path className="cl-journey-path" pathLength="1000" d={d} />
      {points.map((p, i) => (
        <g
          key={`node-${i}`}
          className="cl-journey-node"
          style={{ animationDelay: `${300 + i * 180}ms`, transformOrigin: `${xs[i]}px ${ys[i]}px` }}
        >
          <circle cx={xs[i]} cy={ys[i]} r="7" fill="none" stroke={toneColor(p.tone)} strokeWidth="1.5" />
          <circle cx={xs[i]} cy={ys[i]} r="3" fill={toneColor(p.tone)} />
        </g>
      ))}
      {points.map((p, i) => (
        <g key={`label-${i}`} className="cl-journey-label" style={{ animationDelay: `${500 + i * 180}ms` }}>
          <text
            x={xs[i]}
            y={i % 2 === 0 ? ys[i] - 18 : ys[i] + 26}
            textAnchor={i === 0 ? 'start' : i === N - 1 ? 'end' : 'middle'}
            fontFamily={MONO} fontSize="9" fill={TOKENS.ink3} letterSpacing="0.06em"
            style={{ textTransform: 'uppercase' }}
          >{p.date || ''}</text>
          <text
            x={xs[i]}
            y={i % 2 === 0 ? ys[i] - 18 + 12 : ys[i] + 26 + 12}
            textAnchor={i === 0 ? 'start' : i === N - 1 ? 'end' : 'middle'}
            fontFamily={SANS} fontSize="11" fill={TOKENS.ink}
          >{(p.label || '').slice(0, 26)}</text>
        </g>
      ))}
    </svg>
  );
}

// ── Page ───────────────────────────────────────────────────────────────
export default function Clouseau({ session }) {
  const [competitors, setCompetitors] = useState(null); // null = loading
  const [openId, setOpenId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    const { data: comps, error: ce } = await aws.from('clouseau_competitor')
      .select('*').order('watched_since', { ascending: false });
    if (ce) {
      toast.error('Could not load competitors', ce.message || 'Unknown error');
      setCompetitors([]);
      return;
    }
    if (!comps?.length) { setCompetitors([]); return; }
    const ids = comps.map(c => c.id);
    const { data: sigs } = await aws.from('clouseau_signal')
      .select('*').in('competitor_id', ids)
      .order('detected_at', { ascending: false });
    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
    const sigByComp = {};
    const lastByComp = {};
    for (const s of sigs || []) {
      if (!sigByComp[s.competitor_id]) sigByComp[s.competitor_id] = [];
      sigByComp[s.competitor_id].push(s);
      if (!lastByComp[s.competitor_id]) lastByComp[s.competitor_id] = s.detected_at;
    }
    const enriched = comps.map(c => {
      const all = sigByComp[c.id] || [];
      const todays = all.filter(s => new Date(s.detected_at) >= startOfToday);
      const events = todays.map(s => ({
        id: s.id,
        type: s.type,
        time: relativeTime(s.detected_at),
        line: s.line,
        detail: s.detail,
        source: s.source_label,
        urgent: s.is_urgent,
        urgentNote: s.urgent_note,
      }));
      const isQueued = all.length === 0
        && (Date.now() - new Date(c.watched_since).getTime()) < 24 * 3600 * 1000;
      return {
        id: c.id,
        name: c.name,
        url: c.url,
        mark: { letter: c.mark_letter, bg: c.mark_bg, fg: c.mark_fg },
        watchedSince: fmtMonthYear(c.watched_since),
        events,
        queued: isQueued,
        lastActivityRel: lastByComp[c.id] ? relativeTime(lastByComp[c.id]) : null,
        _row: c,
      };
    });
    setCompetitors(enriched);
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // Single document pointermove listener — spec §6.1 mandates this.
  // Walks every `.cl-eye` (positions pupils) and every `.cl-card` (writes
  // --sx/--sy/--sp custom properties for the spotlight). Reads layout
  // (getBoundingClientRect) but doesn't trigger React renders, so
  // pointer-frame work stays cheap for the ~10 cards we expect.
  useEffect(() => {
    function onMove(e) {
      // Eyes (topbar + first-time empty state).
      const eyes = document.querySelectorAll('.cl-eye');
      for (let i = 0; i < eyes.length; i++) {
        const eye = eyes[i];
        const r = eye.getBoundingClientRect();
        const ecx = r.left + r.width / 2;
        const ecy = r.top + r.height / 2;
        const dx = e.clientX - ecx;
        const dy = e.clientY - ecy;
        const dist = Math.hypot(dx, dy);
        const travel = 5 * Math.min(1, dist / 240);
        const ang = Math.atan2(dy, dx);
        const px = Math.cos(ang) * travel;
        const py = Math.sin(ang) * travel;
        const pupil = eye.firstElementChild;
        if (pupil) pupil.style.transform = `translate(${px.toFixed(2)}px, ${py.toFixed(2)}px)`;
      }
      // Card spotlight.
      const cards = document.querySelectorAll('.cl-card');
      for (let i = 0; i < cards.length; i++) {
        const card = cards[i];
        const r = card.getBoundingClientRect();
        const sx = e.clientX - r.left;
        const sy = e.clientY - r.top;
        // Proximity = 1 when over the card centre, falling to 0 by ~600px away.
        const ccx = r.left + r.width / 2;
        const ccy = r.top + r.height / 2;
        const dist = Math.hypot(e.clientX - ccx, e.clientY - ccy);
        const sp = Math.max(0, 1 - dist / 600);
        card.style.setProperty('--sx', `${sx.toFixed(1)}px`);
        card.style.setProperty('--sy', `${sy.toFixed(1)}px`);
        card.style.setProperty('--sp', sp.toFixed(2));
      }
    }
    document.addEventListener('pointermove', onMove);
    return () => document.removeEventListener('pointermove', onMove);
  }, []);

  // Per spec — sort: urgent first, then any-events, then quiet. Within tier, by recency.
  const sorted = useMemo(() => {
    if (!competitors) return [];
    return [...competitors].sort((a, b) => {
      const aUrg = a.events.some(e => e.urgent) ? 1 : 0;
      const bUrg = b.events.some(e => e.urgent) ? 1 : 0;
      if (aUrg !== bUrg) return bUrg - aUrg;
      if (a.events.length !== b.events.length) return b.events.length - a.events.length;
      return 0;
    });
  }, [competitors]);

  const openComp = openId ? sorted.find(c => c.id === openId) : null;

  return (
    <div style={{
      height: '100vh', display: 'flex', flexDirection: 'column',
      background: TOKENS.canvas, overflow: 'hidden',
    }}>
      <Topbar onAddClick={() => setAddOpen(true)} />
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {competitors === null ? (
          <div style={{ padding: 60, textAlign: 'center', fontFamily: SANS, fontSize: 13, color: TOKENS.ink3 }}>
            Loading…
          </div>
        ) : sorted.length === 0 ? (
          <FirstTimeEmpty onAdd={() => setAddOpen(true)} />
        ) : (
          <>
            <TodaySummary competitors={sorted} />
            <ul className="cl-grid" style={{
              listStyle: 'none', margin: 0,
              padding: '0 36px 48px',
              display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 18,
            }}>
              {sorted.map(c => (
                <CompetitorCard key={c.id} competitor={c} onClick={() => setOpenId(c.id)} />
              ))}
            </ul>
          </>
        )}
      </div>
      {openComp && (
        <Dossier competitorRow={openComp._row} onClose={() => setOpenId(null)} />
      )}
      {addOpen && (
        <AddCompetitorModal
          session={session}
          onClose={() => setAddOpen(false)}
          onAdded={() => load()}
          takenCount={competitors?.length || 0}
        />
      )}
    </div>
  );
}

function FirstTimeEmpty({ onAdd }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '80px 24px',
    }}>
      <div style={{
        textAlign: 'center', maxWidth: 520,
        background: TOKENS.surface, border: `1px solid ${TOKENS.hairline}`,
        borderRadius: 16, padding: '40px 32px',
      }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <EyesGlyph />
          <span style={{ fontFamily: SANS, fontSize: 20, fontWeight: 500, color: TOKENS.ink }}>Clouseau</span>
        </div>
        <div style={{
          fontFamily: SANS, fontSize: 16, color: TOKENS.ink2, lineHeight: 1.55, marginBottom: 24,
        }}>Watch competitors. Catch movement.</div>
        <button type="button" onClick={onAdd} style={{ ...primaryBtn, cursor: 'pointer' }}>
          + Add competitor
        </button>
      </div>
    </div>
  );
}
